import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormHelperText, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DateTimeInput from "components/MDDateTimeInput";
import MDButton from "components/MDButton";
import DataTable from "templates/Tables/DataTable";
import { popUp, deleteMsg } from "components/Custom/AreYouSurePopUp";
// Receipt page theme (shared)
import SectionCard, { ranuButtonSx } from "layouts/receipt/components/SectionCard";
import { SCHEDULE_FIELDS } from "./eventFromUtils";

const FOCUS_GOLD = "#C89B3C";

const SELECT_EVENT_DATES_MSG = "Please select the event start date and end date first";

const tableSx = ({ palette }) => ({
  "& thead th > div": { color: palette.primary.main },

  "@media (max-width: 991.98px)": {
    "& th, & td": { paddingLeft: "8px", paddingRight: "8px" },
    "& td > div": { width: "auto" },
  },
});

const COLUMNS = [
  { Header: "sr no", accessor: "sr", align: "center", width: "8%" },
  ...SCHEDULE_FIELDS.map(({ label, accessor, width }) => ({
    Header: (
      <span>
        {label.toLowerCase()}{" "}
        <MDTypography component="span" variant="button" fontWeight="bold" color="error">
          *
        </MDTypography>
      </span>
    ),
    accessor,
    align: "center",
    width,
  })),
  { Header: "", accessor: "--", align: "center" },
  { Header: "action", accessor: "action", align: "center", width: "10%" },
];

const ScheduleHandle = ({ index }) => {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: index });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: index });

  return (
    <MDBox
      ref={setDropRef}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "2.5rem",
        borderRadius: "8px",
        backgroundColor: isOver && !isDragging ? "rgba(200, 155, 60, 0.25)" : "transparent",
        outline: isOver && !isDragging ? `2px dashed ${FOCUS_GOLD}` : "none",
        transition: "background-color .15s ease",
      }}
    >
      <MDBox
        ref={setDragRef}
        {...attributes}
        {...listeners}
        title="Drag to move this schedule"
        sx={({ palette }) => ({
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          padding: "4px 6px",
          cursor: "grab",
          userSelect: "none",
          touchAction: "none", // lets touch screens drag instead of scrolling
          opacity: isDragging ? 0.4 : 1,
          color: palette.primary.main,
          "&:active": { cursor: "grabbing" },
        })}
      >
        <DragIndicatorIcon fontSize="small" />
        <MDTypography component="span" variant="button" fontWeight="regular" color="text">
          {index + 1}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
};

ScheduleHandle.propTypes = {
  index: PropTypes.number.isRequired,
};

const EventSchedules = ({
  data,
  onAdd,
  onChange,
  onBlur,
  onDelete,
  onReorder,
  getVisibleError,
  eventStartDate,
  eventEndDate,
}) => {
  const theme = useTheme();
  // Phones (< 576px): one card per schedule, fields stacked one below the other
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Rows whose date field was clicked before the event start / end date were selected
  const [dateHintRows, setDateHintRows] = useState({});

  // Once both event dates are selected the hint is not needed any more
  useEffect(() => {
    if (eventStartDate && eventEndDate) setDateHintRows({});
  }, [eventStartDate, eventEndDate]);

  /* ---------------- drag & drop ---------------- */
  const [activeIndex, setActiveIndex] = useState(null);

  // small distance so a simple click on the handle doesn't start a drag
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = ({ active }) => setActiveIndex(active.id);
  const handleDragCancel = () => setActiveIndex(null);
  const handleDragEnd = ({ active, over }) => {
    console.log("drag end", active.id, over?.id, "onReorder:", typeof onReorder);
    setActiveIndex(null);
    if (over && active.id !== over.id && onReorder) onReorder(active.id, over.id);
  };

  const renderField = (field, item, index) => {
    const { name, type } = field;
    const error = getVisibleError(field, index);
    const isDate = type === "date";

    // Schedule dates depend on the event dates: until both are selected, don't open the
    // date picker, show the message under the field instead (mouse click and keyboard open)
    const datesMissing = isDate && (!eventStartDate || !eventEndDate);
    const blockPicker = (e) => {
      if (!datesMissing) return;
      e.stopPropagation();
      e.preventDefault();
      setDateHintRows((prev) => ({ ...prev, [index]: true }));
    };
    const blockPickerKey = (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") blockPicker(e);
    };

    const message = error || (datesMissing && dateHintRows[index] ? SELECT_EVENT_DATES_MSG : "");

    return (
      <MDBox onClickCapture={blockPicker} onKeyDownCapture={blockPickerKey}>
        <DateTimeInput
          name={name}
          type={type}
          fullWidth
          placeholder={type === "text" ? `Enter ${field.label.toLowerCase()}` : undefined}
          value={item[name] || ""}
          onChange={(e) => onChange(e, index)}
          onBlur={() => onBlur(name, index)}
          error={!!message}
          inputProps={isDate ? { min: eventStartDate, max: eventEndDate } : undefined}
        />
        {message && <FormHelperText error>{message}</FormHelperText>}
      </MDBox>
    );
  };

  /* ---------------- desktop / tablet: table ---------------- */
  const rows = data.map((item, index) => ({
    sr: <ScheduleHandle index={index} />,
    ...Object.fromEntries(
      SCHEDULE_FIELDS.map((field) => [field.accessor, renderField(field, item, index)])
    ),
    action: (
      <MDButton color="error" onClick={() => popUp(onDelete, index, deleteMsg("schedule"))}>
        delete
      </MDButton>
    ),
  }));

  /* ---------------- mobile: stacked cards ---------------- */
  const mobileList = (
    <MDBox>
      {data.map((item, index) => (
        <MDBox
          key={index}
          mb={2}
          p={1.5}
          sx={({ palette }) => ({
            border: `1px solid ${palette.secondary.main}`,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
          })}
        >
          <MDBox mb={1} display="flex" justifyContent="flex-start">
            <ScheduleHandle index={index} />
          </MDBox>

          {SCHEDULE_FIELDS.map((field) => (
            <MDBox key={field.name} mb={1.5}>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={({ palette }) => ({
                  display: "block",
                  mb: 0.5,
                  textTransform: "capitalize",
                  color: palette.primary.main,
                })}
              >
                {field.label.toLowerCase()}{" "}
                <MDTypography component="span" variant="button" fontWeight="bold" color="error">
                  *
                </MDTypography>
              </MDTypography>
              {renderField(field, item, index)}
            </MDBox>
          ))}

          <MDButton
            color="error"
            fullWidth
            onClick={() => popUp(onDelete, index, deleteMsg("schedule"))}
          >
            delete
          </MDButton>
        </MDBox>
      ))}
    </MDBox>
  );

  return (
    <SectionCard title="Schedules" subtitle="Schedule dates must be within the event dates">
      <MDBox display="flex" justifyContent="flex-end" mb={1}>
        <MDButton
          color="primary"
          sx={ranuButtonSx}
          startIcon={<AddIcon />}
          fullWidth={isMobile}
          onClick={onAdd}
        >
          Add Schedule
        </MDButton>
      </MDBox>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {isMobile ? (
          mobileList
        ) : (
          /* The table has its own padding, so cancel the card padding around it */
          <MDBox mx={{ xs: "-12px", md: "-16px" }} mb={{ xs: "-12px", md: "-16px" }} sx={tableSx}>
            <DataTable
              table={{ rows, columns: COLUMNS }}
              isSorted={false}
              showTotalEntries={false}
              noEndBorder
              entriesPerPage={{ defaultValue: 1000000, entries: [10, 25, 50, 100, 500] }}
            />
          </MDBox>
        )}

        {/* floating chip that follows the pointer while dragging */}
        <DragOverlay dropAnimation={null}>
          {activeIndex !== null ? (
            <MDBox
              px={2}
              py={1}
              sx={({ palette }) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                border: `2px solid ${FOCUS_GOLD}`,
                boxShadow: "0 6px 18px rgba(0,0,0,.2)",
                color: palette.primary.main,
                cursor: "grabbing",
              })}
            >
              <DragIndicatorIcon fontSize="small" />
              <MDTypography variant="button" fontWeight="bold" color="inherit">
                Schedule {activeIndex + 1}
                {data[activeIndex]?.title ? ` - ${data[activeIndex].title}` : ""}
              </MDTypography>
            </MDBox>
          ) : null}
        </DragOverlay>
      </DndContext>
    </SectionCard>
  );
};

EventSchedules.propTypes = {
  data: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  getVisibleError: PropTypes.func.isRequired,
  eventStartDate: PropTypes.string,
  eventEndDate: PropTypes.string,
};

export default EventSchedules;
